import React from 'react';
import { DepartmentNav } from '../components/DepartmentNav';
import { Card, CardContent } from '../components/ui/card';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { MapPin, Lightbulb, Code, Target, Heart } from '@phosphor-icons/react';
import { SEO } from '../components/SEO';

export const AboutPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="About TurfPulse"
        description="TurfPulse was built by a lawn care enthusiast in Georgetown, TX who wanted science-based lawn care timing using Growing Degree Days. Learn our story and mission."
        keywords="about TurfPulse, lawn care app story, GDD tracking app, Georgetown TX lawn care, growing degree days app"
        path="/about"
      />
      <DepartmentNav />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <TurfPulseLogoSVG size={80} animated={false} />
          </div>
          <h1 className="text-4xl font-bold mb-4">About TurfPulse</h1>
          <p className="text-xl text-muted-foreground">
            Built by a lawn nerd who got tired of guessing.
          </p>
        </div>

        {/* The Story */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Lightbulb className="text-yellow-500" weight="duotone" />
              How It Evolved
            </h2>
            
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                It started with mowing grass. Just that — keeping the yard looking decent 
                after a move from Hawaii to North Austin, Texas. New climate, new grass types, 
                no clue what I was doing.
              </p>

              <p>
                A childhood friend had moved to the area and was running a lawn leveling 
                business. When I needed advice, he'd share what he knew. "What's this weed?" 
                I'd ask. "Try this," he'd say. I worked on his crew for a while — leveling 
                yards across Central Texas, learning the basics.
              </p>
              
              <p>
                Then I found <strong>Ron Henry</strong> and <strong>GCI Matt</strong> on YouTube.
              </p>

              <p>
                That's when simple mowing turned into something else entirely. Pre-emergents, 
                post-emergents, PGR programs, soil amendments, micronutrients — I went from 
                "how often should I cut?" to calibrating backpack sprayers and arguing about 
                base temperatures. Ron and Matt didn't just teach lawn care — they taught 
                the <em>science</em> behind it. Growing Degree Days. Application timing. 
                Product rates. The "why" behind every "when."
              </p>

              <div className="bg-muted/50 rounded-lg p-4 my-6 border-l-4 border-primary">
                <p className="italic">
                  "I went from a guy who just mowed his grass to someone running full-blown 
                  lawn programs — soil tests, granular apps, liquid sprays, growth regulators. 
                  And I still didn't have a single tool that tracked it all in one place."
                </p>
              </div>

              <p>
                Post-it notes on the garage cabinet. Notes in my phone. A spreadsheet that 
                tracked GDD from OpenWeather data. It worked — until the API key expired 
                and I never got around to fixing it.
              </p>
              
              <p>
                That mess of scattered notes and a broken spreadsheet became the blueprint 
                for TurfPulse.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* The Problem */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Target className="text-red-500" weight="duotone" />
              The Gap Nobody Filled
            </h2>
            
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                The deeper I went, the more I realized: no single app did what I needed. 
                I was tracking GDD in a spreadsheet, logging spray dates in my phone, 
                checking weather conditions on three different apps, and calculating 
                mix rates with a calculator. Every. Single. Time.
              </p>
              
              <p>
                I looked at other lawn care apps. Most of them were calendar-based — 
                "apply in April" — ignoring the fact that spring in Austin hits different 
                than spring in Ohio. And the ones tied to GDD? They were really just 
                storefronts pushing their own product lines. The "recommendations" 
                always led back to their shopping cart.
              </p>
              
              <p>
                I didn't need another store. I needed the tool Ron and Matt described 
                in their videos — something <strong>product-agnostic</strong> that just gave me 
                the science and let me choose what to apply.
              </p>
              
              <div className="bg-muted/50 rounded-lg p-4 my-6 border-l-4 border-primary">
                <p className="italic">
                  "On <strong>June 6, 2025</strong>, I created a GDD Google Sheet tracker with an Apps Script 
                  that pulled weather data from OpenWeather. It had columns for GDD accumulation, 
                  product application rates, weather data, spray logs... it worked great for a 
                  few months. Then a 401 error killed it."
                </p>
              </div>
              
              <p>
                That broken spreadsheet became the blueprint for everything TurfPulse is today.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* The Solution */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Code className="text-blue-500" weight="duotone" />
              What TurfPulse Became
            </h2>
            
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                TurfPulse is what happens when a lawn care obsession meets a tech background.
                It evolved from mowing grass into a full-stack platform that automates 
                everything I was doing by hand:
              </p>
              
              <ul className="list-none space-y-3 my-6">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">→</span>
                  <span><strong>Automatic GDD tracking</strong> based on your actual local weather — not estimates</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">→</span>
                  <span><strong>Application logging with photos</strong> so you never forget what you sprayed or how it looked</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">→</span>
                  <span><strong>Mix calculator</strong> that does the math for exact grams, oz, and scoops per gallon</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">→</span>
                  <span><strong>Spray condition forecasts</strong> so you pick the right day, not just the right product</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">→</span>
                  <span><strong>Pest and weed tracking</strong> with GDD-mapped emergence windows</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">→</span>
                  <span><strong>Curated video guides</strong> from creators like Ron Henry, GCI Matt, and Lawn Insider — rotating seasonally</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">→</span>
                  <span><strong>Everything in one place</strong> — no more post-it notes, no more guessing</span>
                </li>
              </ul>
              
              <p>
                It's honestly curated — TurfPulse doesn't manufacture or stock any products; 
                we link to Amazon for the items we'd actually use ourselves, and earn a small 
                affiliate commission at no extra cost to you. It helps you use whatever products 
                <em> you</em> choose, with science-based timing and the math done for you.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* The Mission */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Heart className="text-red-500" weight="duotone" />
              The Mission
            </h2>
            
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                TurfPulse is for the homeowner who wants a better lawn but doesn't 
                have time to become a turfgrass scientist. It's for the weekend warrior 
                who's tired of guessing. It's for anyone who's ever asked "when should 
                I apply this?" and gotten a vague answer.
              </p>
              
              <p>
                <strong>Your lawn, backed by science.</strong> That's not just a tagline — 
                it's literally what this app does. Real weather data. Real GDD calculations. 
                Real timing recommendations.
              </p>
              
              <p>
                No guessing. No missed windows. No wasted product.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Credits */}
        <Card className="mb-8 bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <MapPin className="text-primary" weight="duotone" />
              Built in Central Texas
            </h2>
            
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                TurfPulse was built in the suburbs of Austin, Texas — where summers 
                are brutal, St. Augustine fights for survival, and everyone has an 
                opinion about when to scalp your Bermuda.
              </p>
              
              <p className="font-medium text-foreground">
                The creators who started all of this:
              </p>
              
              <ul className="list-none space-y-2 my-4">
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">→</span>
                  <span><strong>Ron Henry</strong> (Golf Course Lawn) — for teaching the science of weed control, GDD timing, and making lawn care accessible to everyone</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">→</span>
                  <span><strong>GCI Matt</strong> (GCI Turf Academy) — for the deep-dive programs, grub control knowledge, and showing what a real lawn care plan looks like</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary font-bold">→</span>
                  <span><strong>Lawn Insider</strong> (Rhett Bohannon) — for the bermuda expertise and real-world application demos</span>
                </li>
              </ul>

              <p>
                Shoutout to the Central Texas lawn care community — the leveling crews, 
                the lawn nerds on YouTube, and the neighbors who aren't afraid to ask 
                "what are you spraying?" The knowledge sharing in this community is 
                what makes everyone's grass greener.
              </p>
              
              <p className="text-sm pt-4 border-t border-border mt-6">
                Special thanks to <strong>Georgetown Lawn Leveling</strong> for the 
                foundation — the basic knowledge that started this whole journey. 
                Sometimes a simple "try this" is all it takes to spark something bigger.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <div className="text-center text-muted-foreground">
          <p className="mb-2">Questions? Ideas? Just want to talk lawn care?</p>
          <a 
            href="mailto:support@getturfpulse.com" 
            className="text-primary hover:underline font-medium"
          >
            support@getturfpulse.com
          </a>
        </div>

        {/* 🎺 Easter egg — the songbook */}
        <div className="text-center mt-10 text-xs text-muted-foreground" data-testid="about-anthem-easter-egg">
          <a
            href="/about/the-songbook"
            className="inline-flex items-center gap-1 opacity-60 hover:opacity-100 transition-opacity"
            title="Yes, we wrote 5 songs about probe-driven irrigation."
          >
            🎵 we wrote a whole songbook about it →
          </a>
        </div>
      </main>
    </div>
  );
};

export default AboutPage;
